import moment from 'moment';

import { ActivatedRouteSnapshot } from '@angular/router';

import { environment } from '@environment';
import { CONTRIBUTION_TYPE } from '../../../_core/constants/Contribute';
import { COUNTRIES, COUNTRY_NAMES, US_STATES } from '../../../_core/constants/countries';
import { EnvironmentNames } from '../../../_core/constants/environments';
import PageTitles from '../../../_core/constants/page-titles';
import { PaymentMethodType } from '../../../_core/constants/Payment';
import Urls from '../../../_core/constants/urls';
import { Charity } from '../../../_core/models/charity.model';
import { CurrencySet, SearchCharity } from '../../../_core/models/contributeData';
import { Fund } from '../../../_core/models/fund';
import { User } from '../../../_core/models/user';
import {
  RECURRENCE_PATTERN,
  RECURRENCE_RANGE_END,
} from '../../../_shared/recurring-form/recurring-form.models';
import {
  ContributionTypes,
  DAF_PROVIDER,
  PurchaseContributionType,
  TaxDeductionValues,
} from './donate-form.const';

export class ContributionConstData {
  defaultAnonymousDonationValue: boolean;
  availableCountries: SearchCountry[];
  purchaseContributionType: PurchaseContributionType;
  taxDeductionOptions: TaxDeductionOption[];
  userCurrencies: CurrencySet[];
  giverInfo: GiverInfo;
  dafProviderTypes: string[];
  countries: SearchCountry[];
  usStates: string[];
  contactEmail: string;
  homepageLink: string;
  portalLoginLink: string;
  contactLink: string;
  customLink: string;
  utmSource: string;
  signature: string;
  redirectReturnLink: string;
  token: string;
  pageTitle: string;
  purposePlaceholderText: string;
  charityStepIndex: string;
  contributionTypeStepIndex: string;
  financialStepIndex: string;
  giverInfoStepIndex: string;
  wasRedirected: boolean;
  isSwiss: boolean;
  isIgf: boolean;
  isCncf: boolean;
  isGlf: boolean;
  isSinngeber: boolean;
  isTol: boolean;
  isFonte: boolean;
  isPublic: boolean;
  isApplicationAssessment: boolean;

  constructor(currentUser: User, url: string, routeSnapshot: ActivatedRouteSnapshot) {
    this.usStates = US_STATES;
    this.taxDeductionOptions = TaxDeductionValues;
    this.dafProviderTypes = Object.values(DAF_PROVIDER);
    this.availableCountries = [];
    this.isSwiss = environment.fund.environmentName === EnvironmentNames.SWISS;
    this.isIgf = environment.fund.environmentName === EnvironmentNames.IGF;
    this.isCncf = environment.fund.environmentName === EnvironmentNames.CNCF;
    this.isGlf = environment.fund.environmentName === EnvironmentNames.GLF;
    this.isSinngeber = environment.fund.environmentName === EnvironmentNames.SINNGEBER;
    this.isTol = environment.fund.environmentName === EnvironmentNames.TOL;
    this.isFonte = environment.fund.environmentName === EnvironmentNames.FONTE;
    this.contactEmail = environment.fund.mailTo;
    this.homepageLink = environment.fund.homeLink;
    this.portalLoginLink = `${environment.fund.url}/${Urls.AUTH}/${Urls.LOGIN}`;
    this.contactLink = environment.fund.contact;
    this.isPublic = !currentUser;
    if (!this.isPublic) {
      this.userCurrencies = currentUser.currencies;
      this.setGiverInfo(currentUser);
    }
    this.isApplicationAssessment = url.indexOf(Urls.APPLICATION_ASSESSMENT) > -1;
    this.customLink = routeSnapshot.queryParams.id;
    this.utmSource = routeSnapshot.queryParams.utm_source;
    this.signature = routeSnapshot.queryParams.signature;
    this.token = routeSnapshot.params.token;
    this.wasRedirected = !!this.token;
    if (this.wasRedirected) this.setRedirectReturnLink(routeSnapshot.queryParams.wasLoggedIn);
    this.pageTitle = this.setPageTitle();
    this.purposePlaceholderText = this.setPurposePlaceholder();
    this.charityStepIndex = this.isSwiss ? '2' : '1';
    this.contributionTypeStepIndex = this.isSwiss
      ? this.isPublic
        ? '3'
        : '2'
      : this.isPublic
      ? '2'
      : '1';
    this.financialStepIndex = this.isSwiss
      ? this.isPublic
        ? '4'
        : '3'
      : this.isPublic
      ? '3'
      : '2';
    this.giverInfoStepIndex = this.isSwiss
      ? this.isPublic
        ? '5'
        : '4'
      : this.isPublic
      ? '4'
      : '3';
    this.purchaseContributionType = this.setPurchaseContributionType();
  }

  setPageTitle(): string {
    return this.isPublic
      ? this.isApplicationAssessment
        ? PageTitles.APPLICATION_ASSESSMENT
        : PageTitles.CONTRIBUTION_PUBLIC
      : PageTitles.CONTRIBUTION_PRIVATE;
  }

  setPurposePlaceholder(): string {
    return this.isApplicationAssessment
      ? 'contribute.placeholder.purpose_assessment'
      : 'contribute.placeholder.purpose';
  }

  setPurchaseContributionType(): PurchaseContributionType {
    return this.isPublic
      ? this.isApplicationAssessment
        ? this.customLink
          ? PurchaseContributionType.PUBLIC_ASSESSMENT_BUTTON
          : PurchaseContributionType.PUBLIC_ASSESSMENT
        : this.customLink
        ? PurchaseContributionType.PUBLIC_DONATE_BUTTON
        : PurchaseContributionType.PUBLIC_DONATE
      : PurchaseContributionType.PRIVATE;
  }

  setGiverInfo(userInfo: User): void {
    this.giverInfo = {
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      email: userInfo.email,
      streetAddress: userInfo.address?.address1,
      country: COUNTRIES.find((country) => country.name === userInfo.address?.country),
      city: userInfo.address?.city,
      stateProvince: userInfo.address?.state,
      postalCode: userInfo.address?.postalCode,
      isCompany: false,
      companyName: null,
    };
  }

  setRedirectReturnLink(wasLoggedIn: string): void {
    if (wasLoggedIn && !!JSON.parse(wasLoggedIn))
      this.redirectReturnLink = `${environment.fund.redirectSourceUrl}/${Urls.APP}/${Urls.ACTIVITY}`;
    else
      this.redirectReturnLink = `${environment.fund.redirectSourceUrl}/${Urls.PUBLIC}/${Urls.DONATE}`;
  }
}

export class ContributionDynamicData {
  contributionTypeOptions: ContributionType[];
  charitySearchResults: Charity[];
  countrySearchResults: SearchCountry[];
  giverCountrySearchResults: any[];
  availableCurrencies: CurrencySet[];
  currencySearchResults: CurrencySet[];
  redirectTarget: EnvironmentNames;
  swissContributionInfo: NextActionResponse;
  summaryData: ContributeDonorDisplayData;
  nextButtonText: string;
  serverError: string;
  warningMessage: string;
  publicRecurringWarningMessage: string;
  networkMemberInfo: string;
  networkMemberMinimumContribution: number;
  contributionDetailsText: string;
  canDonate: boolean;
  submitted: boolean;
  displayBankDetails: boolean;
  displayTaxDeduction: boolean;
  displayNetworkMember: boolean;
  displaySummary: boolean;
  initiatedRedirect: boolean;
  loading: boolean;
  isRecurring: boolean;
  isPublicRecurring: boolean;
  isPublic: boolean;
  isNetworkMemberInfoLoading: boolean;

  constructor(constData: ContributionConstData, isRecurring = false) {
    this.availableCurrencies = [];
    this.charitySearchResults = [];
    this.countrySearchResults = [];
    this.giverCountrySearchResults = [];
    this.currencySearchResults = [];
    this.nextButtonText = 'common.next';
    this.canDonate = true;
    this.contributionDetailsText = null;
    this.isRecurring = isRecurring;
    this.isPublic = constData.isPublic;
    this.setTenantSpecificContributionTypes();
  }

  setTenantSpecificContributionTypes(): void {
    switch (environment.fund.environmentName) {
      case EnvironmentNames.SINNGEBER:
        this.setContributionTypeOptions(COUNTRY_NAMES.GERMANY, true);
        break;
      case EnvironmentNames.TOL:
        this.setContributionTypeOptions(COUNTRY_NAMES.SOUTH_AFRICA, false);
        break;
      case EnvironmentNames.GES:
        this.setContributionTypeOptions(COUNTRY_NAMES.PUERTO_RICO, false);
        break;
      case EnvironmentNames.FONTE:
        this.setContributionTypeOptions(COUNTRY_NAMES.BRAZIL, false);
        break;
      default:
        this.contributionTypeOptions = [...ContributionTypes];
        this.setCreditCardOnly();
        this.setSepaTenants();
        break;
    }
  }

  setCreditCardOnly(hasCreditCardOption = true): void {
    if (this.isRecurring && hasCreditCardOption && !this.isPublic) {
      this.contributionTypeOptions = ContributionTypes.filter(
        (type) => type.code === CONTRIBUTION_TYPE.CREDIT_CARD
      );
    }
  }

  setWarningMessage(message: string): void {
    switch (message) {
      case 'INVALID_CONTRIBUTION':
        this.warningMessage = 'contribute.message.tax_deduction.canada_warning';
        break;
      case 'COUNTRY_NOT_FOUND':
      case 'FUNDNUMBER_NOT_FOUND':
      case '_NOT_FOUND':
        this.warningMessage = 'error';
        break;
      default:
        this.warningMessage = message;
    }
    this.networkMemberInfo = null;
  }

  setContributionTypeOptions(countryName: string, taxDeduction: boolean): void {
    const baseTypes = [
      CONTRIBUTION_TYPE.BANK_TRANSFER_OFFLINE,
      CONTRIBUTION_TYPE.CREDIT_CARD,
      CONTRIBUTION_TYPE.OTHER_ASSETS,
    ];

    switch (countryName) {
      case COUNTRY_NAMES.NEW_ZEALAND:
        const newZealandTaxTypes = [
          CONTRIBUTION_TYPE.BANK_TRANSFER_OFFLINE,
          CONTRIBUTION_TYPE.OTHER_ASSETS,
        ];
        this.contributionTypeOptions = ContributionTypes.filter((type) =>
          (taxDeduction ? newZealandTaxTypes : baseTypes).includes(type.code)
        );
        break;
      case COUNTRY_NAMES.AUSTRALIA:
      case COUNTRY_NAMES.UNITED_KINGDOM:
        const AustraliaAndUKTaxTypes = [
          CONTRIBUTION_TYPE.BANK_TRANSFER_OFFLINE,
          CONTRIBUTION_TYPE.BANK_TRANSFER_ONLINE,
          CONTRIBUTION_TYPE.CREDIT_CARD,
          CONTRIBUTION_TYPE.OTHER_ASSETS,
        ];
        this.contributionTypeOptions = ContributionTypes.filter((type) =>
          (taxDeduction ? AustraliaAndUKTaxTypes : baseTypes).includes(type.code)
        );
        break;
      case COUNTRY_NAMES.CANADA:
        this.contributionTypeOptions = ContributionTypes.filter((type) =>
          taxDeduction
            ? type.code !== CONTRIBUTION_TYPE.SEPA_DEBIT &&
              type.code !== CONTRIBUTION_TYPE.BANK_TRANSFER_ONLINE
            : baseTypes.includes(type.code)
        ).map((type) =>
          type.code === CONTRIBUTION_TYPE.BANK_TRANSFER_OFFLINE
            ? { ...type, displayName: 'contribute.contribution_type.bank_transfer_offline_cncf' }
            : type
        );
        break;
      case COUNTRY_NAMES.UNITED_STATES:
        this.contributionTypeOptions = [...ContributionTypes].filter(
          (type) => type.code !== CONTRIBUTION_TYPE.SEPA_DEBIT
        );
        break;
      case COUNTRY_NAMES.GERMANY:
        let GermanyTypes = [CONTRIBUTION_TYPE.BANK_TRANSFER_OFFLINE, CONTRIBUTION_TYPE.CREDIT_CARD];
        taxDeduction
          ? (GermanyTypes = [...GermanyTypes, CONTRIBUTION_TYPE.SEPA_DEBIT])
          : GermanyTypes.filter((type) => type !== CONTRIBUTION_TYPE.SEPA_DEBIT);
        if (this.isPublic && environment.fund.environmentName === EnvironmentNames.SINNGEBER)
          GermanyTypes.push(CONTRIBUTION_TYPE.DONOR_ADVISED_FUND);
        this.contributionTypeOptions = ContributionTypes.filter((type) =>
          GermanyTypes.includes(type.code)
        ).sort((a, b) => GermanyTypes.indexOf(a.code) - GermanyTypes.indexOf(b.code));
        break;
      case COUNTRY_NAMES.BRAZIL:
        const BrazilTypes = [
          CONTRIBUTION_TYPE.BANK_TRANSFER_OFFLINE,
          CONTRIBUTION_TYPE.CREDIT_CARD,
        ];
        this.contributionTypeOptions = ContributionTypes.filter((type) =>
          BrazilTypes.includes(type.code)
        );
        break;
      case COUNTRY_NAMES.PUERTO_RICO:
        const PuertoRicoTypes = taxDeduction
          ? [
              CONTRIBUTION_TYPE.BANK_TRANSFER_OFFLINE,
              CONTRIBUTION_TYPE.CREDIT_CARD,
              CONTRIBUTION_TYPE.PHYSICAL_CHECK,
              CONTRIBUTION_TYPE.PUBLICLY_TRADED_SECURITIES,
            ]
          : [
              CONTRIBUTION_TYPE.CREDIT_CARD,
              CONTRIBUTION_TYPE.BANK_TRANSFER_OFFLINE,
              CONTRIBUTION_TYPE.OTHER_ASSETS,
            ];
        this.contributionTypeOptions = ContributionTypes.filter((type) =>
          PuertoRicoTypes.includes(type.code)
        );
        break;
      case COUNTRY_NAMES.CROATIA:
        const CroatiaTypes = taxDeduction
          ? [CONTRIBUTION_TYPE.BANK_TRANSFER_OFFLINE]
          : [CONTRIBUTION_TYPE.BANK_TRANSFER_OFFLINE, CONTRIBUTION_TYPE.CREDIT_CARD];
        this.contributionTypeOptions = ContributionTypes.filter((type) =>
          CroatiaTypes.includes(type.code)
        );
        break;
      case COUNTRY_NAMES.ROMANIA:
        const RomaniaTypes = taxDeduction
          ? [CONTRIBUTION_TYPE.BANK_TRANSFER_OFFLINE]
          : [
              CONTRIBUTION_TYPE.BANK_TRANSFER_OFFLINE,
              CONTRIBUTION_TYPE.CREDIT_CARD,
              CONTRIBUTION_TYPE.OTHER_ASSETS,
            ];
        this.contributionTypeOptions = ContributionTypes.filter((type) =>
          RomaniaTypes.includes(type.code)
        );
        break;
      default:
        this.contributionTypeOptions = ContributionTypes.filter((type) =>
          baseTypes.includes(type.code)
        );
    }
    this.setCreditCardOnly(countryName !== COUNTRY_NAMES.SOUTH_AFRICA);
    this.setSepaTenants();
  }

  setSepaTenants(): void {
    if (
      ![EnvironmentNames.SINNGEBER, EnvironmentNames.SWISS].includes(
        environment.fund.environmentName
      )
    ) {
      this.contributionTypeOptions = this.contributionTypeOptions.filter(
        (type) => type.code !== CONTRIBUTION_TYPE.SEPA_DEBIT
      );
    }
  }

  setContributionTypeOptionsFromApi(contributionTypeCodes: string[]): void {
    if (!contributionTypeCodes || contributionTypeCodes.length === 0) {
      return;
    }

    this.contributionTypeOptions = ContributionTypes.filter((type) =>
      contributionTypeCodes.includes(type.code)
    );

    this.setCreditCardOnly();
    this.setSepaTenants();
  }

  setContributionTypeDependentData(
    contributionType: CONTRIBUTION_TYPE,
    constData: ContributionConstData,
    taxDeduction: boolean,
    countryDonatingFrom: COUNTRY_NAMES
  ): void {
    if (constData.isSinngeber) {
      this.contributionDetailsText =
        constData.isPublic && contributionType === CONTRIBUTION_TYPE.DONOR_ADVISED_FUND
          ? 'contribute.info.donor_advised_giving_fund'
          : null;
      return;
    }
    this.contributionDetailsText = '';
    this.displayBankDetails = false;
    if (!contributionType) return;

    // Define contribution type info mapping
    const contributionTypeInfoMap = {
      [CONTRIBUTION_TYPE.CREDIT_CARD]: 'contribute.info.credit_card',
      [CONTRIBUTION_TYPE.SEPA_DEBIT]: 'contribute.info.sepa_debit',
      [CONTRIBUTION_TYPE.BANK_TRANSFER_ONLINE]: 'contribute.info.bank_transfer_online',
      [CONTRIBUTION_TYPE.OTHER_ASSETS]: 'contribute.info.other_assets_detail',
    };

    switch (contributionType) {
      case CONTRIBUTION_TYPE.PUBLICLY_TRADED_SECURITIES:
        if (constData.isGlf)
          this.contributionDetailsText = 'contribute.info.other_assets';
        if (constData.isCncf)
          this.contributionDetailsText = 'contribute.info.publicly_traded_securities';
        break;
      case CONTRIBUTION_TYPE.PHYSICAL_CHECK:
        if (constData.isCncf) {
          this.displayBankDetails = true;
          this.contributionDetailsText = 'contribute.info.physical_check';
        }
        break;
      case CONTRIBUTION_TYPE.DONOR_ADVISED_FUND:
        if (constData.isCncf) this.displayBankDetails = true;
        break;
      case CONTRIBUTION_TYPE.BANK_TRANSFER_OFFLINE:
        if (constData.isCncf)
          this.contributionDetailsText = 'contribute.info.bank_transfer_offline';
        if (!constData.isPublic || constData.isCncf)
          this.displayBankDetails =
            taxDeduction || countryDonatingFrom === COUNTRY_NAMES.UNITED_STATES ? false : true;
        break;
      case CONTRIBUTION_TYPE.BANK_TRANSFER_ONLINE:
      case CONTRIBUTION_TYPE.CREDIT_CARD:
      case CONTRIBUTION_TYPE.OTHER_ASSETS:
      case CONTRIBUTION_TYPE.SEPA_DEBIT:
        if (constData.isCncf)
          this.contributionDetailsText = contributionTypeInfoMap[contributionType];
        break;
    }
  }

  setCurrencySearchResults(searchText: string): void {
    // Used by old template for currency search functionality
    this.currencySearchResults = this.availableCurrencies.filter(
      (currency) => currency.symbol.indexOf(searchText.toUpperCase()) >= 0
    );
  }

  setNextButtonText(
    contributionType: CONTRIBUTION_TYPE,
    taxDeduction: boolean,
    isSwiss: boolean,
    isCncf: boolean
  ): void {
    if (this.willRedirect) this.nextButtonText = 'common.next';
    else if (isCncf && contributionType === CONTRIBUTION_TYPE.BANK_TRANSFER_ONLINE)
      this.nextButtonText = 'common.submit';
    else if (
      [CONTRIBUTION_TYPE.BANK_TRANSFER_ONLINE, CONTRIBUTION_TYPE.CREDIT_CARD].includes(
        contributionType
      ) &&
      (!isSwiss || !taxDeduction)
    )
      this.nextButtonText = 'common.next';
    else this.nextButtonText = 'common.submit';
  }

  setNetworkMemberInfo(countryName: string, minimumContribution: number): void {
    this.networkMemberMinimumContribution = minimumContribution;

    const countryMessageKeyMap: Record<string, string> = {
      [COUNTRY_NAMES.UNITED_KINGDOM]: 'uk',
      [COUNTRY_NAMES.CANADA]: 'canada',
      [COUNTRY_NAMES.AUSTRALIA]: 'australia',
      [COUNTRY_NAMES.NEW_ZEALAND]: 'new_zealand',
      [COUNTRY_NAMES.NETHERLANDS]: 'netherlands',
      [COUNTRY_NAMES.BRAZIL]: 'brazil',
      [COUNTRY_NAMES.CROATIA]: 'croatia',
      [COUNTRY_NAMES.ROMANIA]: 'romania',
    };

    const countryKey = countryMessageKeyMap[countryName];
    if (!countryKey) {
      this.networkMemberInfo = null;
      this.networkMemberMinimumContribution = null;
      this.warningMessage = null;
      return;
    }

    const suffix = minimumContribution == null ? '_fallback' : '';
    this.networkMemberInfo = `contribute.message.network_member.${countryKey}_minimum${suffix}`;
    this.warningMessage = null;
  }

  setSummary(summaryData: ContributeDonorDisplayData): void {
    this.summaryData = summaryData;
    this.displaySummary = true;
    this.serverError = null;
    this.nextButtonText = 'common.next';
  }

  reset(): void {
    this.serverError = null;
    this.redirectTarget = null;
    this.submitted = false;
    this.displaySummary = false;
    this.swissContributionInfo = null;
    this.nextButtonText = 'common.next';
  }

  get willRedirect(): boolean {
    if (!this.swissContributionInfo) return false;
    return !!this.swissContributionInfo.redirect;
  }

  get redirectTargetLink(): string {
    return `${this.swissContributionInfo.redirect}/${Urls.PUBLIC}/${Urls.DONATE}/${this.swissContributionInfo.token}`;
  }
}

export enum AnonymityOption {
  NAME = 'name',
  NAME_EMAIL = 'nameEmail',
  ANONYMOUS = 'anonymous',
}

export interface AmountLiveValidator {
  searchInProgress: boolean;
  error?: boolean;
  minAmount?: string;
}

export interface ContributionType {
  code: CONTRIBUTION_TYPE;
  displayName: string;
}

export interface TokenData {
  currencies: CurrencySet[];
  data: RepopulateData;
}

export interface CurrencyCheck {
  value: string;
  zeroDecimalCurrency: boolean;
}

export interface ContributeDonorDisplayData {
  charityName: string;
  contributionInfo: ContributionInfo;
  contributionType: CONTRIBUTION_TYPE;
  assetDescription: string;
  dafProvider: string;
  fundName: string;
  financialInfo: FinancialInfo;
  giverInfo: GiverInfo;
  recurrencePattern?: RECURRENCE_PATTERN;
  startDate?: string;
  endDate?: string;
}

interface GiverInfo {
  city: string;
  companyName?: string;
  country: CodeName;
  email: string;
  firstName: string;
  isCompany: boolean;
  lastName: string;
  middleName?: string;
  postalCode: string;
  stateProvince: string;
  streetAddress: string;
}

interface FinancialInfo {
  amount: number;
  anonymousDonation: boolean;
  contributionPurpose: string;
  currency: any;
}

interface ContributionInfo {
  charityInternalId: string;
  charityName: string;
  countryDonatingFrom: SearchCountry;
  taxDeduction: TaxDeductionOption;
  taxDeductionFund?: string;
}

export interface RepopulateData {
  amount: string;
  anonymousDonation: boolean;
  assetDescription: string;
  city: string;
  company: boolean;
  companyName: string;
  contributionPurpose: string;
  contributionType: string;
  country: string;
  currencySymbol: string;
  dafProvider: string;
  email: string;
  entityName: string;
  firstName: string;
  fundName: string;
  fundNumberInternalId: string;
  fundNumber: string;
  fundNumberDisplay?: string;
  lastName: string;
  middleName: string;
  postalCode: string;
  purchaseContributionType: PurchaseContributionType;
  state: string;
  streetAddress: string;
  recurrencePattern?: RECURRENCE_PATTERN;
  startDate?: number;
  endDate?: number;
  rangeOption: RECURRENCE_RANGE_END;
  rangeOccurrences: number;
  shareDonorDetails: boolean;
}

export interface CustomLinkFund {
  cseg5: string;
  entityName: string;
  fundName: string;
  fundNumber: string;
  fundNumberDisplay?: string;
  fundType: string;
  status: string;
  vendorName: string;
  name?: string;
}

export interface SearchCountry {
  uuid: string;
  name: string;
  taxDeduction: boolean;
  taxDeductionOptions: TaxDeductionOption[];
  currencySet: CurrencySet[];
  mainCurrency: string;
  description: string;
  logoUrl: string;
  networkMember: NetworkMember;
}

export interface NetworkMember {
  name: string;
  logoUrl: string;
  description: string;
  confirmationText: string;
  withRecurring: boolean;
  config: { minimumContribution: number };
  contriConfig: {
    logoUrl: string | null;
    description: string | null;
    redirectUrl: string | null;
    currencyUuid: string | null;
    taxDeductible: boolean;
    /**
     * Child Tenant Portal URL — set only for Child Tenant Managed network members, and the single
     * editable source of their redirect destination. The configurator mirrors it into
     * `redirectUrl`, which stays the field the runtime reads (`networkMemberRedirectUrl`).
     */
    childTenantUrl: string | null;
    successMessage: string | null;
    minimumContribution: number | null;
    enableDafContributions: boolean;
    postContributionAction: string | null;
    contributionPurposeRequired: boolean;
    enableRecurringContributions: boolean;
    contributionPurposeFieldLabel: string | null;
    taxDeductibleContributionTypes: string[];
    contributionPurposeDefaultValue: string | null;
    taxDeductibleRequirementsMessage: string | null;
    nonTaxDeductibleContributionTypes: string[];
    taxDeductibleContributionMinimumAmountRequired: number | null;
  } | null;
}

export interface CodeName {
  code?: string;
  uuid?: string;
  name: string;
}

export interface TaxDeductionOption {
  code: boolean;
  value: string;
}

export interface NextActionResponse {
  text1: string;
  text2: string;
  stripeAvailable: boolean;
  hyperLink: string;
  bankTransferInstructions: BankTransferInstruction[];
  token: string;
  redirect: string;
  logoUrl: string;
}

interface BankTransferInstruction {
  currency: string;
  bankName: string;
  address: string;
  accountName: string;
  accountAddress: string;
  iban: string;
  sortCode: string;
  swift: string;
  accountNumber: string;
  bsbCode: string;
}

export class CheckContributionPayload {
  countryName: string;
  fundNumber: string;
  taxDeduction: boolean;
  donateButton: boolean;
  fundType: string;

  constructor(
    countryName: string,
    fundNumber: string,
    taxDeduction: boolean,
    donateButton: boolean,
    fundType: string
  ) {
    this.countryName = countryName || 'country';
    this.fundNumber = fundNumber;
    this.taxDeduction = taxDeduction;
    this.donateButton = donateButton;
    this.fundType = fundType;
  }
}

export class BaseContributionPayload {
  anonymousDonation: boolean;
  assetDescription: string;
  city: string;
  contactEmail: string;
  contributionPurpose: string;
  contributionType: CONTRIBUTION_TYPE;
  company: boolean;
  companyName: string;
  country: string;
  countryUuid: string;
  currencyUuid: string;
  dafProvider: string;
  fundName: string;
  fundType: string;
  firstName: string;
  fundNumberInternalId: string;
  lastName: string;
  middleName: string;
  purchaseContributionType: string;
  stateProvince: string;
  streetAddress: string;
  taxDeduction: boolean;
  zipCode: string;
  shareDonorDetails: boolean;
  networkMember: string;

  constructor(formData: any, charity: Fund | SearchCharity, constData: ContributionConstData) {
    const isTolWithBTOffline =
      constData.isTol &&
      formData.contributionTypeInfo.contributionType.code ===
        CONTRIBUTION_TYPE.BANK_TRANSFER_OFFLINE;
    this.anonymousDonation = formData.financialInfo.anonymousDonation;
    this.assetDescription = formData.contributionTypeInfo.assetDescription;
    this.city = formData.giverInfo.city;
    this.contactEmail = formData.giverInfo.email;
    this.contributionPurpose = formData.financialInfo.contributionPurpose;
    this.contributionType = formData.contributionTypeInfo.contributionType.code;
    this.company = formData.giverInfo.isCompany;
    this.companyName = formData.giverInfo.companyName;
    this.country =
      formData.countryAndCharityInfo.countryDonatingFrom?.name || formData.giverInfo.country.name;
    this.countryUuid =
      constData.isSwiss || isTolWithBTOffline
        ? formData.countryAndCharityInfo.countryDonatingFrom?.uuid
        : null;
    this.currencyUuid =
      constData.isSwiss || isTolWithBTOffline ? formData.financialInfo.currency.uuid : null;
    this.dafProvider = formData.contributionTypeInfo.dafProvider;
    this.fundName =
      formData.contributionTypeInfo.fundName || (charity as SearchCharity).name || charity.fundName;
    this.fundType = charity.fundType;
    this.firstName = formData.giverInfo.firstName;
    this.fundNumberInternalId = charity.id || charity.cseg5;
    this.lastName = formData.giverInfo.lastName;
    this.middleName = formData.giverInfo.middleName;
    this.purchaseContributionType = constData.purchaseContributionType;
    this.stateProvince = formData.giverInfo.stateProvince;
    this.streetAddress = formData.giverInfo.streetAddress;
    this.taxDeduction =
      constData.isSwiss || constData.isTol
        ? formData.countryAndCharityInfo.taxDeduction?.code || false
        : false;
    this.zipCode = formData.giverInfo.postalCode || '';
    this.shareDonorDetails = formData.giverInfo.shareDonorDetails === AnonymityOption.NAME_EMAIL;
    this.networkMember = charity.networkMember;
  }
}

export class GetNextActionPayload extends BaseContributionPayload {
  amount: number;
  currencySymbol: string;
  donateButton: boolean;
  entityName: string;
  fundNumber: string;
  publishToNetsuite: boolean;
  recurrencePattern: RECURRENCE_PATTERN;
  startDate: number;
  endDate: number;
  rangeOption: RECURRENCE_RANGE_END;
  rangeOccurrences: number;

  constructor(
    formData: any,
    charity: Fund | SearchCharity,
    constData: ContributionConstData,
    publish: boolean = false
  ) {
    super(formData, charity, constData);
    this.amount = formData.financialInfo.amount;
    this.currencySymbol = formData.financialInfo.currency.symbol;
    this.donateButton = !!constData.customLink;
    this.entityName = (charity as SearchCharity).name || charity.fundName;
    this.fundNumber = charity.fundNumber;
    this.publishToNetsuite = publish;
    this.taxDeduction = formData.countryAndCharityInfo.taxDeduction?.code || null;

    if (formData.financialInfo.recurrenceForm) {
      const { recurrencePattern, recurrenceRange } = formData.financialInfo.recurrenceForm;
      const { startDate, endDate, rangeOption, rangeOccurrences } = recurrenceRange;

      this.recurrencePattern = recurrencePattern || null;
      this.startDate = startDate || null;
      this.endDate = endDate || null;
      this.rangeOption = rangeOption || null;
      this.rangeOccurrences = rangeOccurrences || null;
    }
  }
}

export class PaymentPayload extends BaseContributionPayload {
  accessToken: string = '';
  accountId: string;
  currency: string;
  fundNumberDigits: string;
  fundNumberDigitsDisplay: string;
  name: string = 'donation';
  paymentMethodType: PaymentMethodType;
  publicToken: string;
  quantity: number = 1;
  redirectToSwiss: boolean;
  requestDate: string = moment().utc().valueOf().toString();
  sendingBankName: string = '';
  unitAmount: number;
  recurrencePattern?: RECURRENCE_PATTERN;
  startDate?: number;
  endDate?: number;
  charity?: string;
  donationSource?: string | null = null;
  constructor(
    formData: any,
    charity: Fund | SearchCharity,
    constData: ContributionConstData,
    publicToken?: string,
    accountId?: string
  ) {
    super(formData, charity, constData);
    this.accountId = accountId || null;
    this.currency = formData.financialInfo.currency.symbol;
    this.fundNumberDigits = charity.fundNumber;
    this.fundNumberDigitsDisplay = charity.fundNumberDisplay || charity.fundNumber;
    this.paymentMethodType = this.computeMethodType(
      formData.contributionTypeInfo.contributionType.code,
      constData
    );
    this.publicToken = publicToken || null;
    this.redirectToSwiss = constData.wasRedirected || false;
    this.unitAmount = formData.financialInfo.amount;
    this.recurrencePattern = formData.financialInfo.recurrenceForm?.recurrencePattern || null;
    this.startDate = formData.financialInfo.recurrenceForm?.recurrenceRange?.startDate || null;
    this.endDate = formData.financialInfo.recurrenceForm?.recurrenceRange?.endDate || null;
    this.charity = charity?.fundName || (charity as SearchCharity)?.name;
    this.shareDonorDetails = constData.wasRedirected
      ? formData.giverInfo.shareDonorDetails
      : formData.giverInfo.shareDonorDetails === AnonymityOption.NAME_EMAIL;
  }

  computeMethodType(
    contributionType: CONTRIBUTION_TYPE,
    constData: ContributionConstData
  ): PaymentMethodType {
    switch (contributionType) {
      case CONTRIBUTION_TYPE.CREDIT_CARD:
        return PaymentMethodType.CARD;
      case CONTRIBUTION_TYPE.SEPA_DEBIT:
        return PaymentMethodType.SEPA_DEBIT;
      case CONTRIBUTION_TYPE.BANK_TRANSFER_ONLINE:
        if (constData.isCncf) return PaymentMethodType.ACSS_DEBIT;
        return PaymentMethodType.PLAID_BANK_TRANSFER;
      default:
        return null;
    }
  }
}

export interface RedirectTarget {
  tenantId: string;
  environmentUrl: string;
  name: EnvironmentNames;
}
